import React, { useEffect, useState } from 'react';
import { BsChevronDown, BsChevronRight, BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Pagination from '../../../components/Pagination';
import { createErrorToast, createSuccessToast } from '../../../redux/modules/app';
import { showConfirmModal } from '../../../redux/modules/modal';
import { useDeleteImportFiles, useImportFileList } from '../../../services/import-service';

import ImportStatusBadge from './ImportStatusBadge';
import { formatDateTime } from './util';

const STATUS_FILTERS = ['', 'created', 'active', 'error'];

const formatSize = bytes => {
  if (!bytes) {
    return '—';
  }

  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * The staged files with search, a status filter, and removal of one file, the
 * selected files or everything. Removing an object also drops it from the bucket.
 */
const ImportFilesTable = ({ importId, canEdit, stagedTotal }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(false);

  const { data, isLoading, isFetching } = useImportFileList(importId, { status: status || null, search: debouncedSearch || null, pageNumber }, { enabled: open });
  const { mutateAsync: deleteFiles, isLoading: deleting } = useDeleteImportFiles(importId);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageNumber(1);
    }, 300);

    return () => clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    setSelected([]);
  }, [data]);

  const files = data?.data ?? [];
  const allOnPageSelected = files.length > 0 && files.every(file => selected.includes(file.id));

  const remove = async ({ ids = null, all = false }, label) => {
    try {
      const result = await deleteFiles({ ids, all });

      dispatch(createSuccessToast({ title: `${result.deleted} file(s) removed`, description: label }));
    } catch (error) {
      dispatch(createErrorToast({ title: 'Files could not be removed', description: error?.message, error }));
    }
  };

  const handleRemoveOne = file => remove({ ids: [file.id] }, file.path);

  const handleRemoveSelected = () => {
    dispatch(showConfirmModal(`Remove ${selected.length} selected file(s) from the import?`, () => remove({ ids: selected }, 'Selection removed')));
  };

  const handleRemoveAll = () => {
    dispatch(showConfirmModal(`Remove all ${stagedTotal} staged file(s) from the import? The manifest is kept.`, () => remove({ all: true }, 'All staged files removed')));
  };

  const toggle = id => setSelected(current => (current.includes(id) ? current.filter(entry => entry !== id) : [...current, id]));

  const togglePage = () => setSelected(allOnPageSelected
    ? selected.filter(id => !files.some(file => file.id === id))
    : [...new Set([...selected, ...files.map(file => file.id)])]);

  if (stagedTotal === 0 && !search && !status) {
    return null;
  }

  return (
    <Box mt={4}>
      <Flex alignItems="center" justifyContent="space-between" mb={2}>
        <Button
          aria-expanded={open}
          data-testid="toggle-files"
          leftIcon={<Icon as={open ? BsChevronDown : BsChevronRight} />}
          size="sm"
          variant="textGrey"
          onClick={() => setOpen(current => !current)}
        >
          {`Staged files (${stagedTotal})`}
        </Button>

        {open && (
        <Flex alignItems="center" gap={2}>
          <Input data-testid="file-search" placeholder="Search paths" size="sm" value={search} w="220px" onChange={event => setSearch(event.target.value)} />
          <Select data-testid="file-status-filter" size="sm" value={status} w="140px" onChange={event => { setStatus(event.target.value); setPageNumber(1); }}>
            {STATUS_FILTERS.map(option => <option key={option || 'all'} value={option}>{option || 'All statuses'}</option>)}
          </Select>
          {canEdit && selected.length > 0 && (
            <Button data-testid="remove-selected" isLoading={deleting} size="sm" variant="secondary" onClick={handleRemoveSelected}>
              {`Remove ${selected.length} selected`}
            </Button>
          )}
          {canEdit && stagedTotal > 0 && (
            <Button data-testid="remove-all" isLoading={deleting} size="sm" variant="textGrey" onClick={handleRemoveAll}>
              Remove all
            </Button>
          )}
        </Flex>
        )}
      </Flex>

      <Collapse unmountOnExit in={open}>
        {isLoading && <Text>Loading...</Text>}

        {!isLoading && files.length === 0 && <Text color="greyScale.500" textStyle="bodySm">No staged files match.</Text>}

        {files.length > 0 && (
        <Box opacity={isFetching ? 0.6 : 1} overflowX="auto">
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                {canEdit && (
                  <Th w="32px">
                    <Checkbox data-testid="select-page" isChecked={allOnPageSelected} onChange={togglePage} />
                  </Th>
                )}
                <Th>Path</Th>
                <Th>Size</Th>
                <Th>Type</Th>
                <Th>Status</Th>
                <Th>Uploaded</Th>
                {canEdit && <Th />}
              </Tr>
            </Thead>
            <Tbody>
              {files.map(file => (
                <Tr data-testid={`import-file-${file.id}`} key={file.id}>
                  {canEdit && (
                    <Td>
                      <Checkbox data-testid={`select-file-${file.id}`} isChecked={selected.includes(file.id)} onChange={() => toggle(file.id)} />
                    </Td>
                  )}
                  <Td maxW="420px" wordBreak="break-all">{file.path}</Td>
                  <Td>{formatSize(file.size)}</Td>
                  <Td>{file.contentType ?? '—'}</Td>
                  <Td>
                    <ImportStatusBadge kind="row" status={file.status} />
                    {file.error && <Text color="red.600" textStyle="bodySm">{file.error}</Text>}
                  </Td>
                  <Td>{formatDateTime(file.uploadedAt)}</Td>
                  {canEdit && (
                    <Td>
                      <IconButton
                        aria-label={`remove ${file.path}`}
                        data-testid={`remove-file-${file.id}`}
                        icon={<BsTrash />}
                        isDisabled={deleting}
                        size="xs"
                        variant="textGrey"
                        onClick={() => handleRemoveOne(file)}
                      />
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          </Table>

          {data?.lastPage > 1 && (
            <Pagination
              currentPage={data.currentPage}
              from={data.from}
              handlePageSelection={setPageNumber}
              pageCount={data.lastPage}
              perPage={data.perPage}
              to={data.to}
            />
          )}
        </Box>
        )}
      </Collapse>
    </Box>
  );
};

ImportFilesTable.propTypes = {
  importId: PropTypes.number.isRequired,
  canEdit: PropTypes.bool.isRequired,
  stagedTotal: PropTypes.number.isRequired
};

export default ImportFilesTable;
